/**
 * Tenant Onboarding - Connection Type Selection + Admin Consent Flow
 * 
 * Flow:
 * 1. Select connection type (Intune or SCCM Import)
 * 2. If Intune: Admin consent → Auto-sync → Complete
 * 3. If SCCM: Placeholder for future manual import
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { parseRestoreConsentState } from "@/components/EnterpriseAppPermissionsCard";
import { parseServiceTierConsentState } from "@/components/ServiceTierCard";
import {
  Shield, 
  Check, 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  ExternalLink, 
  Building2, 
  Lock,
  Cloud,
  Server,
  ArrowRight,
  RefreshCw,
  AlertTriangle
} from "lucide-react";

// Admin consent returns here. Every ALICE app registration must list this as
// a redirect URI: which registration the admin consents to depends on the plan.
const REDIRECT_URI = `${window.location.origin}/onboarding`;

// Plans (service tiers). The client ID and permission list for each come from
// GET /tenant/onboarding/service-tiers, so consent always targets the app
// registration the backend will use for this tenant.
const SERVICE_TIER_STORAGE_KEY = "alice_service_tier";
const DEFAULT_SERVICE_TIER = "full";

// Microsoft echoes the consent `state` back. It carries the chosen plan, so
// the return leg registers the tenant on the plan the admin consented to.
const readConsentTier = (stateParam) => {
  try {
    const decoded = stateParam ? JSON.parse(atob(stateParam)) : null;
    if (decoded?.serviceTier) return decoded.serviceTier;
  } catch {
    // Not our state, or unreadable: fall back to the session copy.
  }
  return sessionStorage.getItem(SERVICE_TIER_STORAGE_KEY) || DEFAULT_SERVICE_TIER;
};

// Connection type options
const CONNECTION_TYPES = {
  INTUNE: "intune",
  SCCM: "sccm"
};

// Step indicator component
const StepIndicator = ({ label, isActive, isComplete }) => (
  <div className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
    isActive ? 'bg-white/15 border border-white/30' : 
    isComplete ? 'bg-green-500/10 border border-green-500/30' : 
    'bg-white/10 border border-white/15'
  }`}>
    {isComplete ? (
      <CheckCircle2 className="h-4 w-4 text-green-400" />
    ) : isActive ? (
      <Loader2 className="h-4 w-4 text-white animate-spin" />
    ) : (
      <div className="h-4 w-4 rounded-full border-2 border-white/20" />
    )}
    <span className={`text-xs font-medium ${
      isActive ? 'text-white/90' : 
      isComplete ? 'text-green-400' : 
      'text-white/70'
    }`}>
      {label}
    </span>
  </div>
);

export default function TenantOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, getAccessToken, getArmToken, demoMode, selectTenant } = useAuth();
  // Invitations the login flow found but could not sign the user into
  // (e.g. MFA required for external access). Shown so the user knows why
  // they were brought here rather than to the tenant they expected.
  const blockedTenants = location.state?.blockedTenants || [];
  
  // Flow state
  const [step, setStep] = useState("select"); // "select" | "consent" | "readiness" | "syncing" | "storage" | "complete"
  const [connectionType, setConnectionType] = useState(null);
  // Plan chosen on the "tier" step, and the plans this server offers.
  const [serviceTier, setServiceTier] = useState(
    () => sessionStorage.getItem(SERVICE_TIER_STORAGE_KEY) || null
  );
  const [serviceTiers, setServiceTiers] = useState(null);
  const [serviceTiersError, setServiceTiersError] = useState(null);
  
  // Consent state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consentComplete, setConsentComplete] = useState(false);
  const [activating, setActivating] = useState(false);
  
  // Readiness check state
  const [readinessProgress, setReadinessProgress] = useState(0);
  const [readinessMessage, setReadinessMessage] = useState("Waiting for Azure AD to propagate permissions...");
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncStartedAt, setSyncStartedAt] = useState(null);
  const [hasTriggeredSync, setHasTriggeredSync] = useState(false);
  
  // Storage configuration state
  const [storageConfig, setStorageConfig] = useState({
    auth_mode: "shared_key",
    storage_account_name: "",
    storage_subscription_id: "",
    storage_resource_group: "",
    connection_string: "",
    container_name: "intunewin-packages",
  });
  const [testingStorage, setTestingStorage] = useState(false);
  const [savingStorage, setSavingStorage] = useState(false);
  // ALICE service-principal info (object ID + display name) in customer
  // tenant — fetched only when the user toggles to Entra mode in the
  // wizard, used by the help panel to show the exact role + assignee.
  const [aliceSp, setAliceSp] = useState(null);
  const [aliceSpError, setAliceSpError] = useState(null);
  const [loadingAliceSp, setLoadingAliceSp] = useState(false);

  // Poll sync status during onboarding
  const pollSyncStatus = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      const response = await axios.get(`${API}/discover/sync-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSyncStatus(response.data);
      
      // Check if sync is complete
      // Either: our sync completed (syncStartedAt check) OR status is completed with 100% progress
      const isComplete = response.data.status === "completed" && response.data.progress >= 100;
      
      if (isComplete) {
        // Verify this isn't stale data - check if completed_at is recent (within last 30 minutes)
        const completedAt = response.data.completed_at ? new Date(response.data.completed_at) : null;
        const isRecent = completedAt && (new Date() - completedAt) < 30 * 60 * 1000; // 30 minutes
        const isOurSync = syncStartedAt && completedAt && completedAt > syncStartedAt;
        
        if (isOurSync || isRecent) {
          setSyncing(false);
          setStep("storage"); // Go to storage configuration step
          toast.success("Apps synced! Now let's configure storage.");
        }
      }
    } catch (err) {
      console.error("Error polling sync status:", err);
    }
  }, [getAccessToken, syncStartedAt]);

  // Poll while syncing
  useEffect(() => {
    let interval;
    if (syncing && step === "syncing") {
      interval = setInterval(pollSyncStatus, 2000);
    }
    return () => clearInterval(interval);
  }, [syncing, step, pollSyncStatus]);

  // Lazily fetch ALICE's SP Object ID inside the customer tenant when the
  // wizard is on the storage step in Entra mode. Required by the help
  // panel so the admin can copy the Object ID into Azure Portal IAM.
  useEffect(() => {
    if (step !== "storage") return;
    if (storageConfig.auth_mode !== "entra") return;
    if (aliceSp || loadingAliceSp || aliceSpError) return;
    const run = async () => {
      setLoadingAliceSp(true);
      try {
        const token = await getAccessToken();
        const r = await axios.get(
          `${API}/tenant/onboarding/alice-service-principal`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAliceSp(r.data);
      } catch (e) {
        setAliceSpError(e.response?.data?.detail || e.message || "Could not look up ALICE service principal");
      } finally {
        setLoadingAliceSp(false);
      }
    };
    run();
  }, [step, storageConfig.auth_mode]);

  // Load the plans when the admin reaches the plan or consent step.
  useEffect(() => {
    if (step !== "tier" && step !== "consent") return;
    if (serviceTiers || serviceTiersError) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const r = await axios.get(`${API}/tenant/onboarding/service-tiers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setServiceTiers(Array.isArray(r.data?.tiers) ? r.data.tiers : []);
      } catch (e) {
        if (!cancelled) {
          setServiceTiersError(e.response?.data?.detail || e.message || "Could not load ALICE plans");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, serviceTiers, serviceTiersError, getAccessToken]);

  // Check for admin consent callback parameters - only once
  useEffect(() => {
    // Settings > Enterprise App Permissions reuses this page's redirect URI
    // for admin consent. Hand that round trip straight back to Settings
    // rather than running onboarding for a tenant that is already active.
    // Settings > Integrations > ALICE Plan also reuses this redirect URI, to
    // approve a new plan's app registration for an already active tenant.
    if (parseServiceTierConsentState(searchParams.get("state"))) {
      const next = new URLSearchParams({ section: "integrations" });
      if (searchParams.get("admin_consent") === "True") {
        next.set("plan_consent", "granted");
      } else {
        next.set("plan_consent", "error");
        const reason = searchParams.get("error_description") || searchParams.get("error");
        if (reason) next.set("plan_consent_error", reason.slice(0, 300));
      }
      navigate(`/settings?${next.toString()}`, { replace: true });
      return;
    }

    if (parseRestoreConsentState(searchParams.get("state"))) {
      const next = new URLSearchParams({ section: "integrations" });
      if (searchParams.get("admin_consent") === "True") {
        next.set("permissions_consent", "granted");
      } else {
        next.set("permissions_consent", "error");
        const reason = searchParams.get("error_description") || searchParams.get("error");
        if (reason) next.set("permissions_consent_error", reason.slice(0, 300));
      }
      navigate(`/settings?${next.toString()}`, { replace: true });
      return;
    }

    // Prevent duplicate processing
    if (consentComplete || activating || hasTriggeredSync) return;

    const adminConsent = searchParams.get("admin_consent");
    const tenant = searchParams.get("tenant");
    const errorParam = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (errorParam) {
      setError(errorDescription || errorParam);
      return;
    }

    if (adminConsent === "True" && tenant) {
      // Admin consent was successful - restore connection type from session
      const savedType = sessionStorage.getItem("alice_connection_type");
      if (savedType) {
        setConnectionType(savedType);
      }
      // The plan the admin consented to travels in the consent state.
      const consentedTier = readConsentTier(searchParams.get("state"));
      setServiceTier(consentedTier);
      setConsentComplete(true);
      handleConsentSuccess(tenant, consentedTier);
    }
  }, [searchParams, consentComplete, activating, hasTriggeredSync]);

  const handleConsentSuccess = async (tenantId, consentedTier = DEFAULT_SERVICE_TIER) => {
    setActivating(true);
    setStep("consent");
    
    try {
      const token = await getAccessToken();
      
      // Register the tenant in ALICE
      const registerResponse = await fetch(`${API}/tenant/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          display_name: user?.email?.split("@")[1]?.split(".")[0] || "Organization",
          admin_email: user?.email,
          permission_profile: "standard",
          connection_type: connectionType || "intune",
          // The plan the admin consented to, so ALICE uses the app
          // registration that holds this tenant's permissions.
          service_tier: consentedTier || DEFAULT_SERVICE_TIER,
        }),
      });

      if (!registerResponse.ok) {
        let errorMessage = "Failed to register tenant";
        try {
          const err = await registerResponse.json();
          errorMessage = err.detail || errorMessage;
        } catch {
          errorMessage = `Registration failed: ${registerResponse.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // The user may have been signed into a tenant they were invited to.
      // From here on every request (readiness check, initial sync, storage
      // setup) must target the tenant just registered -- their own.
      let registeredTenantId = user?.tenantId;
      try {
        const registered = await registerResponse.json();
        registeredTenantId = registered?.tenant_id || registeredTenantId;
      } catch {
        // Body is informational; fall back to the account's tenant id.
      }
      if (registeredTenantId) {
        selectTenant(registeredTenantId);
      }

      // Activate the tenant (consent has been granted)
      const activateResponse = await fetch(`${API}/tenant/activate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!activateResponse.ok) {
        let errorMessage = "Failed to activate tenant";
        try {
          const err = await activateResponse.json();
          errorMessage = err.detail || errorMessage;
        } catch {
          errorMessage = `Activation failed: ${activateResponse.statusText}`;
        }
        throw new Error(errorMessage);
      }

      toast.success("Tenant connected! Preparing for initial sync...");
      setActivating(false);
      
      // Wait for Azure AD enterprise app to be fully provisioned
      // This uses active polling instead of a fixed delay
      setStep("readiness");
      setReadinessProgress(0);
      setReadinessMessage("Initializing permission check...");
      
      const isReady = await waitForEnterpriseAppReady(token);
      
      if (!isReady) {
        toast.error("Enterprise app setup is taking longer than expected. You can sync manually from the Discover page.");
        setStep("complete");
        return;
      }
      
      // Automatically start the Intune sync
      await startInitialSync(token);
      
    } catch (err) {
      console.error("Activation error:", err);
      setError(err.message);
      setActivating(false);
    }
  };

  // Wait for enterprise app to be ready before starting sync
  const waitForEnterpriseAppReady = async (token, maxAttempts = 12) => {
    const POLL_INTERVAL = 5000; // 5 seconds between checks
    const INITIAL_DELAY = 10000; // 10 second initial delay before first check
    
    // Initial delay to give Azure AD time to start propagating
    setReadinessMessage("Waiting for Azure AD to propagate permissions...");
    setReadinessProgress(5);
    await new Promise(resolve => setTimeout(resolve, INITIAL_DELAY));
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Calculate progress (10% to 90% during polling)
        const progress = 10 + Math.round((attempt / maxAttempts) * 80);
        setReadinessProgress(progress);
        setReadinessMessage(`Verifying enterprise app permissions (${attempt}/${maxAttempts})...`);
        
        console.log(`Readiness check attempt ${attempt}/${maxAttempts}...`);
        
        const response = await axios.get(`${API}/discover/readiness-check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const { ready, status, message, can_retry } = response.data;
        
        if (ready) {
          console.log("Enterprise app is ready for sync");
          setReadinessProgress(100);
          setReadinessMessage("Permissions verified successfully!");
          toast.success("Azure AD permissions confirmed!");
          await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause to show success
          return true;
        }
        
        // If not ready and can retry, continue polling
        if (can_retry && attempt < maxAttempts) {
          console.log(`Not ready yet (${status}): ${message}. Retrying in ${POLL_INTERVAL/1000}s...`);
          setReadinessMessage(message || `Azure AD still provisioning... (${Math.round((attempt / maxAttempts) * 100)}%)`);
          
          // Show toast progress to user every few attempts
          if (attempt % 4 === 0) {
            toast.info(`Still waiting for Azure AD... (${Math.round((attempt / maxAttempts) * 100)}%)`);
          }
          
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
        } else if (!can_retry) {
          // Non-retryable error
          console.error(`Non-retryable error: ${status} - ${message}`);
          setReadinessMessage(message || "Failed to verify permissions");
          toast.error(message);
          return false;
        }
        
      } catch (err) {
        console.error(`Readiness check error (attempt ${attempt}):`, err);
        setReadinessMessage("Connection error, retrying...");
        
        // On network errors, keep retrying
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
        }
      }
    }
    
    // Max attempts reached
    console.warn("Max readiness check attempts reached");
    setReadinessMessage("Verification timed out");
    return false;
  };

  const startInitialSync = async (token, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 10000; // 10 seconds between retries
    
    // Prevent duplicate sync triggers (only on first attempt)
    if (retryCount === 0 && hasTriggeredSync) {
      console.log("Sync already triggered, skipping duplicate");
      return;
    }
    
    if (retryCount === 0) {
      setHasTriggeredSync(true);
      setStep("syncing");
      setSyncing(true);
      setSyncStartedAt(new Date());
    }
    
    try {
      await axios.post(`${API}/discover/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Polling will handle the rest
      pollSyncStatus();
      
    } catch (err) {
      console.error(`Failed to start sync (attempt ${retryCount + 1}):`, err);
      
      // Check if sync is already in progress (409 Conflict)
      if (err.response?.status === 409) {
        // Sync already running - just poll its status
        console.log("Sync already in progress, polling existing sync...");
        toast.info("Sync already in progress. Monitoring...");
        pollSyncStatus();
        return;
      }
      
      // Check if it's an auth/permission error that might resolve with retry
      const isRetryableError = err.response?.status === 401 || 
                               err.response?.status === 403 ||
                               err.response?.status === 500 ||
                               err.response?.status === 502 ||
                               err.response?.status === 503;
      
      if (isRetryableError && retryCount < MAX_RETRIES) {
        const nextRetry = retryCount + 1;
        toast.warning(`Sync failed, retrying in ${RETRY_DELAY/1000}s... (attempt ${nextRetry}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return startInitialSync(token, nextRetry);
      }
      
      // For other errors or max retries reached, allow user to proceed but warn them
      toast.error("Failed to start initial sync. You can sync manually from the Discover page.");
      setStep("complete");
      setSyncing(false);
    }
  };

  const handleSelectConnectionType = (type) => {
    setConnectionType(type);
    sessionStorage.setItem("alice_connection_type", type);
    
    if (type === CONNECTION_TYPES.INTUNE) {
      setStep("tier");
    } else if (type === CONNECTION_TYPES.SCCM) {
      setStep("sccm_placeholder");
    }
  };

  const handleSelectTier = (tier) => {
    setServiceTier(tier);
    sessionStorage.setItem(SERVICE_TIER_STORAGE_KEY, tier);
    setStep("consent");
  };

  const selectedTier = (serviceTiers || []).find((t) => t.tier === serviceTier) || null;

  const initiateAdminConsent = () => {
    if (!selectedTier?.available || !selectedTier?.client_id) {
      toast.error("This plan isn't available yet. Go back and choose another plan.");
      return;
    }
    setLoading(true);
    
    // Generate state for CSRF protection
    const state = btoa(JSON.stringify({
      timestamp: Date.now(),
      returnUrl: "/dashboard",
      connectionType: connectionType,
      serviceTier: selectedTier.tier
    }));
    
    // Store state in session for verification
    sessionStorage.setItem("alice_consent_state", state);

    // Build admin consent URL
    const consentUrl = new URL("https://login.microsoftonline.com/common/adminconsent");
    // The chosen plan's app registration: a reduced plan consents to ALICE's
    // second registration, never to the sign-in one.
    consentUrl.searchParams.set("client_id", selectedTier.client_id);
    consentUrl.searchParams.set("redirect_uri", REDIRECT_URI);
    consentUrl.searchParams.set("state", state);

    // Redirect to Microsoft admin consent
    window.location.href = consentUrl.toString();
  };

  // Returning from a Settings permission restore: the effect above is
  // forwarding to Settings, so don't flash the onboarding steps meanwhile.
  if (
    parseRestoreConsentState(searchParams.get("state")) ||
    parseServiceTierConsentState(searchParams.get("state"))
  ) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 text-white animate-spin" />
      </div>
    );
  }

  // Demo mode bypass
  if (demoMode) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="h-6 w-6 text-white" />
              Demo Mode Active
            </CardTitle>
            <CardDescription className="text-white/90">
              Tenant onboarding is not required in demo mode.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-white mb-4">
              You're running ALICE in demo mode with placeholder Azure credentials. 
              Full multi-tenant onboarding requires production Azure AD configuration.
            </p>
            <Button 
              onClick={() => navigate("/dashboard")}
              className="w-full bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
            >
              Continue to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-red-400" />
              Connection Failed
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-red-400">{error}</p>
            </div>
            <p className="text-white/90 text-sm">
              This may happen if the admin consent was cancelled or if you don't have 
              the required administrator permissions in your organization.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setError(null);
                  setStep("select");
                  setConnectionType(null);
                }}
                className="flex-1 bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate("/login")}
                className="flex-1 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Readiness check (waiting for Azure AD propagation)
  if (step === "readiness") {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-blue-400/20 animate-pulse" />
                <div className="relative bg-white/20 backdrop-blur-[5px] rounded-full p-4">
                  <Shield className="h-10 w-10 text-white" />
                </div>
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              Verifying Permissions
            </CardTitle>
            <CardDescription className="text-white/90">
              Please wait while we verify Azure AD has processed your admin consent
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Progress */}
            <div className="bg-white/10 rounded-lg p-4 border border-white/15">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-400 uppercase tracking-wide">
                  Azure AD Setup
                </span>
                <span className="text-lg font-bold text-white">
                  {readinessProgress}%
                </span>
              </div>
              <Progress value={readinessProgress} className="h-2 bg-white/20" />
              <p className="text-sm text-white mt-3 font-medium">
                {readinessMessage}
              </p>
            </div>
            
            {/* What's happening */}
            <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-white/10 rounded-lg p-4 space-y-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-white" />
                What's happening?
              </h4>
              <ul className="text-white/90 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <Check className={`h-4 w-4 mt-0.5 ${readinessProgress > 5 ? 'text-green-400' : 'text-white/70'}`} />
                  <span>Admin consent granted to ALICE</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className={`h-4 w-4 mt-0.5 ${readinessProgress > 30 ? 'text-green-400' : 'text-white/70'}`} />
                  <span>Service principal created in your tenant</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className={`h-4 w-4 mt-0.5 ${readinessProgress >= 100 ? 'text-green-400' : 'text-white/70'}`} />
                  <span>Permissions verified with Microsoft Graph</span>
                </li>
              </ul>
            </div>
            
            {/* Info */}
            <div className="flex items-start gap-3 bg-white/10 border border-white/20 rounded-lg p-3">
              <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-200">
                <strong>This is normal.</strong> Azure AD typically takes 30-60 seconds to fully propagate 
                enterprise application permissions across their infrastructure.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Syncing in progress
  if (step === "syncing") {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-white/15 animate-ping" />
                <div className="relative bg-white/20 backdrop-blur-[5px] rounded-full p-4">
                  <RefreshCw className="h-10 w-10 text-white animate-spin" />
                </div>
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              Setting Up Your Environment
            </CardTitle>
            <CardDescription className="text-white/90">
              We're syncing your Intune apps. This may take a few minutes.
            </CardDescription>
            {syncStatus?.estimated_time && (
              <p className="text-white text-sm font-medium mt-2">
                {syncStatus.estimated_time}
              </p>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Progress */}
            <div className="bg-white/10 rounded-lg p-4 border border-white/15">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white uppercase tracking-wide">
                  {syncStatus?.phase || "Processing"}
                </span>
                <span className="text-lg font-bold text-white">
                  {syncStatus?.progress || 0}%
                </span>
              </div>
              <Progress value={syncStatus?.progress || 0} className="h-2 bg-white/20" />
              <p className="text-sm text-white mt-3 font-medium">
                {syncStatus?.current_step || "Initializing sync..."}
              </p>
            </div>
            
            {/* Steps */}
            <div className="grid grid-cols-2 gap-3">
              <StepIndicator 
                label="Fetching from Intune" 
                isActive={syncStatus?.phase === "Fetching from Intune"}
                isComplete={["Processing & Rationalisation", "Processing & Rationalization", "CVE Matching", "Winget Matching", "Completing", "Complete"].includes(syncStatus?.phase)}
              />
              <StepIndicator 
                label="Rationalisation" 
                isActive={syncStatus?.phase === "Processing & Rationalisation" || syncStatus?.phase === "Processing & Rationalization"}
                isComplete={["CVE Matching", "Winget Matching", "Completing", "Complete"].includes(syncStatus?.phase)}
              />
              <StepIndicator 
                label="CVE Matching" 
                isActive={syncStatus?.phase === "CVE Matching"}
                isComplete={["Winget Matching", "Completing", "Complete"].includes(syncStatus?.phase)}
              />
              <StepIndicator 
                label="Winget Matching" 
                isActive={syncStatus?.phase === "Winget Matching"}
                isComplete={["Completing", "Complete"].includes(syncStatus?.phase)}
              />
            </div>
            
            {/* Warning */}
            <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200">
                <strong>Please don't close this page.</strong> We're preparing your environment 
                for first use.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Storage Configuration
  if (step === "storage") {
    const guidRe = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

    // Client-side validation. Mode-aware: connection-string mode only needs
    // the connection string + container name; Entra mode needs the
    // sub/RG/account triple so ALICE knows which storage account to bind
    // to and (if the auto path is used) what scope to assign the role on.
    const validateStorageInputs = () => {
      const cname = (storageConfig.container_name || "").trim();
      if (cname && (!/^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/.test(cname) || cname.includes("--"))) {
        return "Container Name must be 3-63 lowercase letters, digits and single hyphens.";
      }

      if (storageConfig.auth_mode === "shared_key") {
        const cs = (storageConfig.connection_string || "").trim();
        if (!cs || !cs.includes("AccountName=") || !cs.includes("AccountKey=")) {
          return (
            "Paste the full storage-account connection string from Azure " +
            "Portal → Storage account → Access keys. It must contain both " +
            "AccountName= and AccountKey=."
          );
        }
        return null;
      }

      // Entra mode validation
      const sub = (storageConfig.storage_subscription_id || "").trim();
      const rg = (storageConfig.storage_resource_group || "").trim();
      const acct = (storageConfig.storage_account_name || "").trim();
      if (!guidRe.test(sub)) {
        return "Subscription ID must be a GUID (e.g. 00000000-0000-0000-0000-000000000000).";
      }
      if (!rg || rg.length > 90 || !/^[A-Za-z0-9._\-()]+$/.test(rg) || rg.endsWith(".")) {
        return "Resource Group is not a valid Azure resource group name.";
      }
      if (!/^[a-z0-9]{3,24}$/.test(acct)) {
        if (acct && acct.toLowerCase() === rg.toLowerCase()) {
          return (
            "Storage Account Name must be different from the Resource Group. " +
            "Storage account names are 3-24 lowercase letters/digits, e.g. 'camwoodinternal'."
          );
        }
        return (
          "Storage Account Name must be 3-24 characters, lowercase letters " +
          "and digits only (no hyphens, no uppercase). It's the value at the " +
          "very end of the storage account's resource ID."
        );
      }
      return null;
    };

    // Connection-string path: ALICE saves the config and the same call
    // performs a live connectivity check, so the user gets immediate
    // feedback. This is the recommended default.
    const handleSaveSharedKey = async () => {
      const err = validateStorageInputs();
      if (err) { toast.error(err); return; }
      setSavingStorage(true);
      try {
        const token = await getAccessToken();
        await axios.post(
          `${API}/settings/azure-blob`,
          {
            auth_mode: "shared_key",
            storage_account_name: (storageConfig.storage_account_name || "").trim().toLowerCase() || undefined,
            container_name: (storageConfig.container_name || "intunewin-packages").trim(),
            connection_string: storageConfig.connection_string.trim(),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Storage connected. ALICE is ready to upload packages.");
        setStep("complete");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to save configuration");
      } finally {
        setSavingStorage(false);
      }
    };

    // Entra path — automatic: ALICE uses the admin's ARM token to assign
    // the role on the customer's behalf. Requires Owner / UAA on the
    // storage account. Falls back to the manual path on 403.
    const handleEntraAuto = async () => {
      const err = validateStorageInputs();
      if (err) { toast.error(err); return; }
      setSavingStorage(true);
      try {
        const token = await getAccessToken();
        let armToken;
        try {
          armToken = await getArmToken();
        } catch (e) {
          toast.error(
            "Could not get Azure Resource Manager permission. Either accept the " +
            "Microsoft consent prompt and retry, or use the manual option below."
          );
          return;
        }

        const resp = await axios.post(
          `${API}/tenant/onboarding/assign-storage-role`,
          {
            storage_subscription_id: storageConfig.storage_subscription_id.trim(),
            storage_resource_group: storageConfig.storage_resource_group.trim(),
            storage_account_name: storageConfig.storage_account_name.trim().toLowerCase(),
            container_name: (storageConfig.container_name || "intunewin-packages").trim(),
          },
          { headers: { Authorization: `Bearer ${token}`, "X-Arm-Token": armToken } }
        );

        const data = resp.data || {};
        toast.success(
          data.message ||
          (data.status === "success"
            ? "ALICE was granted access to your storage account."
            : "Storage role assigned. Azure RBAC may take ~60s to propagate.")
        );
        setStep("complete");
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.message) {
          toast.error(detail.message, { duration: 12000 });
        } else if (typeof detail === "string") {
          toast.error(detail);
        } else {
          toast.error("Failed to grant ALICE access to your storage account.");
        }
      } finally {
        setSavingStorage(false);
      }
    };

    // Entra path — manual: persist the storage details so ALICE knows
    // where to look. Connectivity will fail with 403 until the admin
    // (or a colleague with Owner/UAA) adds the role assignment in the
    // Azure Portal using the Object ID shown in the help panel.
    const handleEntraManual = async () => {
      const err = validateStorageInputs();
      if (err) { toast.error(err); return; }
      setSavingStorage(true);
      try {
        const token = await getAccessToken();
        await axios.post(
          `${API}/settings/azure-blob?skip_test=true`,
          {
            auth_mode: "entra",
            storage_account_name: storageConfig.storage_account_name.trim().toLowerCase(),
            storage_subscription_id: storageConfig.storage_subscription_id.trim(),
            storage_resource_group: storageConfig.storage_resource_group.trim(),
            container_name: (storageConfig.container_name || "intunewin-packages").trim(),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(
          "Storage details saved. Add the role in Azure Portal as shown above, " +
          "then verify under Settings → Azure Blob Storage → Test Connection."
        );
        setStep("complete");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to save configuration");
      } finally {
        setSavingStorage(false);
      }
    };

    const handleSaveStorage = () => {
      if (storageConfig.auth_mode === "shared_key") return handleSaveSharedKey();
      return handleEntraAuto();
    };

    const isEntra = storageConfig.auth_mode === "entra";

    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-blue-500/20 rounded-full p-4">
                <Cloud className="h-12 w-12 text-blue-400" />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              Configure Azure Storage
            </CardTitle>
            <CardDescription className="text-white/90">
              Store your packages in Azure Blob Storage for persistence and scalability.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Auth mode toggle */}
            <div>
              <label className="text-sm font-medium text-white block mb-1.5">
                Authentication Method
              </label>
              <div className="flex gap-2 flex-wrap" data-testid="onboarding-storage-auth-mode-toggle">
                <Button
                  type="button"
                  variant={!isEntra ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStorageConfig({ ...storageConfig, auth_mode: "shared_key" })}
                  data-testid="onboarding-auth-mode-shared-key"
                  className={!isEntra
                    ? "bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71]"
                    : "bg-white/10 text-white border-white/20 hover:bg-white/20"}
                >
                  Connection string (recommended)
                </Button>
                <Button
                  type="button"
                  variant={isEntra ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStorageConfig({ ...storageConfig, auth_mode: "entra" })}
                  data-testid="onboarding-auth-mode-entra"
                  className={isEntra
                    ? "bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71]"
                    : "bg-white/10 text-white border-white/20 hover:bg-white/20"}
                >
                  Microsoft Entra ID (advanced)
                </Button>
              </div>
              <p className="text-xs text-white/80 mt-1">
                {isEntra
                  ? "Keyless. ALICE authenticates with your Entra ID; requires the 'Storage Blob Data Contributor' role on this storage account."
                  : "Quickest path. Paste the connection string from Azure Portal → Storage account → Access keys."}
              </p>
            </div>

            {/* Shared-key form */}
            {!isEntra && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-white block mb-1.5">
                    Connection String
                  </label>
                  <textarea
                    data-testid="onboarding-storage-connection-string"
                    placeholder="DefaultEndpointsProtocol=https;AccountName=camwoodinternal;AccountKey=...;EndpointSuffix=core.windows.net"
                    value={storageConfig.connection_string}
                    onChange={(e) => setStorageConfig({ ...storageConfig, connection_string: e.target.value })}
                    rows={3}
                    className="w-full rounded-md bg-white/10 border border-white/20 text-white font-mono text-xs p-2"
                  />
                  <p className="text-xs text-white/80 mt-1">
                    Azure Portal → Storage account → Security + networking → Access keys → copy "Connection string" under key1 or key2.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-white block mb-1.5">
                    Container Name
                  </label>
                  <Input
                    data-testid="onboarding-storage-container-name"
                    placeholder="intunewin-packages"
                    value={storageConfig.container_name}
                    onChange={(e) => setStorageConfig({ ...storageConfig, container_name: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                  />
                  <p className="text-xs text-white/80 mt-1">Will be created automatically if it doesn't exist.</p>
                </div>
              </div>
            )}

            {/* Entra form */}
            {isEntra && (
              <>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">Subscription ID</label>
                    <Input
                      data-testid="onboarding-storage-subscription-id"
                      placeholder="00000000-0000-0000-0000-000000000000"
                      value={storageConfig.storage_subscription_id}
                      onChange={(e) => setStorageConfig({ ...storageConfig, storage_subscription_id: e.target.value })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">Resource Group</label>
                    <Input
                      data-testid="onboarding-storage-resource-group"
                      placeholder="rg-alice-prod"
                      value={storageConfig.storage_resource_group}
                      onChange={(e) => setStorageConfig({ ...storageConfig, storage_resource_group: e.target.value })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">Storage Account Name</label>
                    <Input
                      data-testid="onboarding-storage-account-name"
                      placeholder="camwoodinternal"
                      value={storageConfig.storage_account_name}
                      onChange={(e) => setStorageConfig({ ...storageConfig, storage_account_name: e.target.value })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                    <p className="text-xs text-white/80 mt-1">
                      3-24 lowercase letters/digits. <strong>Not the resource group.</strong>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">Container Name</label>
                    <Input
                      data-testid="onboarding-storage-container-name"
                      placeholder="intunewin-packages"
                      value={storageConfig.container_name}
                      onChange={(e) => setStorageConfig({ ...storageConfig, container_name: e.target.value })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                {/* Help panel: exact role + ALICE Object ID for manual IAM assignment */}
                <div
                  data-testid="onboarding-alice-sp-info"
                  className="rounded-md border border-blue-300/40 bg-blue-500/10 p-3 text-xs text-white/90 space-y-2"
                >
                  <div className="font-semibold text-white">Add this role assignment in Azure Portal:</div>
                  <ol className="list-decimal list-inside space-y-0.5 text-white/85">
                    <li>Storage account → <strong>Access control (IAM)</strong> → <strong>+ Add → Add role assignment</strong></li>
                    <li>Role: <code className="bg-black/30 px-1 rounded">Storage Blob Data Contributor</code></li>
                    <li>Assign access to: <em>User, group, or service principal</em></li>
                    <li>Members → paste the Object ID below into the search</li>
                    <li>Review + assign</li>
                  </ol>
                  <div className="rounded border border-white/20 bg-white/10 p-2 mt-2">
                    {loadingAliceSp ? (
                      <div className="flex items-center gap-2 text-white/80">
                        <Loader2 className="h-3 w-3 animate-spin" /> Looking up ALICE in your tenant…
                      </div>
                    ) : aliceSp ? (
                      <div className="space-y-1">
                        <div>
                          <span className="text-white/70">Display name: </span>
                          <span className="text-white font-medium" data-testid="onboarding-alice-sp-display-name">
                            {aliceSp.display_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/70 shrink-0">Object ID:</span>
                          <code className="font-mono text-[11px] text-white break-all" data-testid="onboarding-alice-sp-object-id">
                            {aliceSp.object_id}
                          </code>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="ml-auto bg-white/10 text-white border-white/20 hover:bg-white/20 h-7 px-2 text-[11px]"
                            onClick={() => {
                              navigator.clipboard.writeText(aliceSp.object_id);
                              toast.success("Object ID copied");
                            }}
                            data-testid="onboarding-copy-alice-sp-object-id"
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-white/80">
                        Could not look up ALICE. {aliceSpError ? `(${aliceSpError})` : ""}{" "}
                        Search Enterprise applications for the display name you set during onboarding.
                      </div>
                    )}
                  </div>
                  <div className="text-white/75">
                    If your account is <em>Owner</em> or <em>User Access Administrator</em> on this storage account,
                    you can skip the portal and click <strong>Try automatic role assignment</strong> instead.
                  </div>
                </div>
              </>
            )}

            {/* Action buttons */}
            {!isEntra && (
              <Button
                data-testid="onboarding-storage-save-btn"
                onClick={handleSaveSharedKey}
                disabled={savingStorage}
                className="w-full bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
              >
                {savingStorage ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</>
                ) : ("Connect & finish")}
              </Button>
            )}

            {isEntra && (
              <div className="grid gap-2">
                <Button
                  data-testid="onboarding-storage-save-btn"
                  onClick={handleEntraAuto}
                  disabled={savingStorage}
                  className="w-full bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                >
                  {savingStorage ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Granting access...</>
                  ) : ("Try automatic role assignment")}
                </Button>
                <Button
                  data-testid="onboarding-storage-manual-btn"
                  onClick={handleEntraManual}
                  disabled={savingStorage}
                  variant="outline"
                  className="w-full bg-white/10 text-white border-white/20 hover:bg-white/20"
                >
                  I'll add the role manually — save details
                </Button>
              </div>
            )}

            <Button
              variant="ghost"
              onClick={() => setStep("complete")}
              className="w-full text-white/80 hover:text-white hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]"
            >
              Skip for now (configure in Settings later)
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Complete
  if (step === "complete") {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-green-500/20 rounded-full p-4">
                <CheckCircle2 className="h-12 w-12 text-green-400" />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              Setup Complete!
            </CardTitle>
            <CardDescription className="text-white/90">
              Your environment is ready. You can now start managing your applications.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {syncStatus?.total_apps > 0 && (
              <div className="bg-white/10 border border-white/15 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-white">{syncStatus.total_apps}</p>
                <p className="text-white/90 text-sm">Applications discovered</p>
              </div>
            )}
            
            <Button
              onClick={() => navigate("/discover")}
              className="w-full h-12 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold text-base"
            >
              Go to Discover
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: SCCM Placeholder
  if (step === "sccm_placeholder") {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-amber-500/20 rounded-full p-4">
                <Server className="h-12 w-12 text-amber-400" />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              SCCM Import Mode
            </CardTitle>
            <CardDescription className="text-white/90">
              Coming Soon
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-amber-400 font-medium">Feature In Development</h4>
                  <p className="text-white/90 text-sm mt-1">
                    SCCM Import mode will allow you to upload reports from System Center 
                    Configuration Manager to discover and manage your applications without 
                    an Intune connection.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-white/10 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2">Planned Features:</h4>
              <ul className="text-white/90 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-white/70 mt-0.5" />
                  <span>Upload SCCM Asset Intelligence reports</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-white/70 mt-0.5" />
                  <span>Manual application inventory management</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-white/70 mt-0.5" />
                  <span>Package creation without Intune deployment</span>
                </li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setStep("select");
                  setConnectionType(null);
                }}
                className="flex-1 bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
              >
                Back
              </Button>
              <Button 
                onClick={() => handleSelectConnectionType(CONNECTION_TYPES.INTUNE)}
                className="flex-1 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
              >
                Use Intune Instead
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: choose a plan (Intune selected)
  if (step === "tier") {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] shadow-[0_8px_24px_rgba(0,0,0,0.15)]">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white/10 rounded-full">
                <Shield className="h-12 w-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">Choose your ALICE plan</CardTitle>
            <CardDescription className="text-white/90 text-base">
              Your plan decides what ALICE can do in your tenant, and which permissions you&apos;ll be asked to approve.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {serviceTiersError ? (
              <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-300 shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-white">
                  <p className="font-medium mb-1">Couldn&apos;t load plans</p>
                  <p className="text-white/80">{String(serviceTiersError)}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setServiceTiersError(null)}
                  className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC]"
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Retry
                </Button>
              </div>
            ) : !serviceTiers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4" data-testid="service-tier-options">
                {serviceTiers.map((tier) => (
                  <button
                    key={tier.tier}
                    type="button"
                    disabled={!tier.available}
                    onClick={() => tier.available && handleSelectTier(tier.tier)}
                    data-testid={`service-tier-${tier.tier}`}
                    className={`text-left p-5 rounded-xl border-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] transition-all group ${
                      tier.available
                        ? "border-white/10 hover:border-white/30 hover:bg-white/10"
                        : "border-white/5 opacity-60 cursor-not-allowed"
                    }`}
                  >
                    <h3 className="text-white font-semibold mb-2">{tier.label}</h3>
                    <p className="text-white/80 text-sm mb-4">{tier.description}</p>
                    <ul className="text-white/80 text-xs space-y-1">
                      <li className="flex items-center gap-2">
                        <Check className="h-3 w-3 text-green-400" />
                        Apps Management
                      </li>
                      <li className="flex items-center gap-2">
                        {tier.includes_os_patch ? (
                          <Check className="h-3 w-3 text-green-400" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-white/40" />
                        )}
                        {tier.includes_os_patch
                          ? tier.os_patch_read_only
                            ? "OS Patch Management, read-only"
                            : "OS Patch Management"
                          : "No OS Patch Management"}
                      </li>
                    </ul>
                    {tier.available ? (
                      <div className="mt-4 flex items-center text-white text-sm font-medium group-hover:translate-x-1 transition-transform">
                        Choose plan
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </div>
                    ) : (
                      <p className="mt-4 text-xs text-amber-300">Not available yet</p>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setStep("select");
                  setConnectionType(null);
                }}
                className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                data-testid="service-tier-back"
              >
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Consent flow (Intune selected)
  if (step === "consent") {
    // Show activating state
    if (consentComplete || activating) {
      return (
        <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
          <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-green-400" />
                Tenant Connected
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
              </div>
              <p className="text-white text-center">
                Admin consent granted successfully. Activating your tenant...
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // The consent step needs a plan: its client ID decides which ALICE app
    // registration the admin approves.
    if (!selectedTier) {
      return (
        <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
          <Card className="w-full max-w-lg bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-white/10">
            <CardContent className="space-y-4 pt-6">
              {!serviceTiers && !serviceTiersError ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 text-white animate-spin" />
                </div>
              ) : (
                <>
                  <p className="text-white text-center">Choose a plan before connecting to Microsoft Intune.</p>
                  <Button
                    onClick={() => setStep("tier")}
                    className="w-full bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                  >
                    Choose a plan
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    // Consent form
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] shadow-[0_8px_24px_rgba(0,0,0,0.15)]">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white/10 rounded-full">
                <Cloud className="h-12 w-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              Connect to Microsoft Intune
            </CardTitle>
            <CardDescription className="text-white/90 text-base">
              Approve the permissions for your plan: {selectedTier.label}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* User info */}
            <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-white/10 rounded-lg p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-white/15 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-lg">
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{user?.name || user?.email}</p>
                <p className="text-white/90 text-sm">{user?.email}</p>
              </div>
            </div>

            {/* What happens */}
            <div className="bg-white/10 border border-white/15 rounded-lg p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5" />
                What happens next?
              </h3>
              <ul className="text-white space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-white mt-0.5 flex-shrink-0" />
                  <span>You'll be redirected to Microsoft's consent page</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-white mt-0.5 flex-shrink-0" />
                  <span>Review and approve the permissions ALICE needs</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-white mt-0.5 flex-shrink-0" />
                  <span><strong>Automatic sync</strong> will discover your Intune apps</span>
                </li>
              </ul>
            </div>

            {/* Permissions list */}
            <div>
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Lock className="h-5 w-5 text-white/90" />
                Required Permissions
              </h3>
              <div className="space-y-2">
                {(selectedTier.permissions || []).map((perm) => (
                  <div key={perm.name} className="flex items-start gap-3 p-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg">
                    <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white text-sm font-mono">{perm.name}</p>
                      <p className="text-white/80 text-xs">{perm.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setStep("tier");
                }}
                className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
              >
                Back
              </Button>
              <Button
                onClick={initiateAdminConsent}
                disabled={loading}
                className="flex-1 h-12 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold text-base"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    Connect with Microsoft
                    <ExternalLink className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <p className="text-white/70 text-xs text-center">
              Requires a Global Administrator or Privileged Role Administrator
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Select connection type (default)
  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] shadow-[0_8px_24px_rgba(0,0,0,0.15)]">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-white/10 rounded-full">
              <Building2 className="h-12 w-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl text-white">
            Connect Your Organization
          </CardTitle>
          <CardDescription className="text-white/90 text-base">
            Choose how you want to discover and manage applications
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* User info */}
          <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-white/10 rounded-lg p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-white/15 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-lg">
                {user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
              </span>
            </div>
            <div>
              <p className="text-white font-medium">{user?.name || user?.email}</p>
              <p className="text-white/90 text-sm">{user?.email}</p>
            </div>
          </div>

          {blockedTenants.length > 0 && (
            <div
              className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-4 flex gap-3"
              data-testid="blocked-invitations-notice"
            >
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-white font-medium mb-1">
                  You have been invited to {blockedTenants.length === 1 ? "an organization" : "organizations"} in ALICE, but could not be signed in:
                </p>
                <ul className="text-white/80 space-y-0.5">
                  {blockedTenants.map((t) => (
                    <li key={t.tenant_id}>
                      <span className="font-medium text-white">{t.display_name}</span>: {t.reason}
                    </li>
                  ))}
                </ul>
                <p className="text-white/70 mt-2">
                  {blockedTenants.some((t) => /mfa|multi-factor/i.test(t.reason || ""))
                    ? "Sign out and sign in again with multi-factor authentication to use that invitation, or connect your own organization below."
                    : "Contact that organization's administrator to restore your access, or connect your own organization below."}
                </p>
              </div>
            </div>
          )}

          {/* Connection options */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Intune Option */}
            <button
              onClick={() => handleSelectConnectionType(CONNECTION_TYPES.INTUNE)}
              className="text-left p-6 rounded-xl border-2 border-white/10 hover:border-white/30 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] hover:bg-white/10 transition-all group"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-white/15 rounded-lg group-hover:bg-white/20 transition-colors">
                  <Cloud className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Microsoft Intune</h3>
                  <span className="text-xs text-white font-medium">Recommended</span>
                </div>
              </div>
              <p className="text-white/90 text-sm mb-4">
                Connect directly to Intune for automatic app discovery, packaging, and deployment.
              </p>
              <ul className="text-white/80 text-xs space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-green-400" />
                  Automatic app discovery
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-green-400" />
                  Direct deployment to Intune
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-green-400" />
                  Real-time sync with tenant
                </li>
              </ul>
              <div className="mt-4 flex items-center text-white text-sm font-medium group-hover:translate-x-1 transition-transform">
                Select Intune
                <ArrowRight className="h-4 w-4 ml-2" />
              </div>
            </button>

            {/* SCCM Option */}
            <button
              onClick={() => handleSelectConnectionType(CONNECTION_TYPES.SCCM)}
              className="text-left p-6 rounded-xl border-2 border-white/10 hover:border-amber-500/50 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] hover:bg-amber-500/5 transition-all group relative"
            >
              <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500/20 rounded text-xs text-amber-400 font-medium">
                Coming Soon
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-500/20 rounded-lg group-hover:bg-amber-500/30 transition-colors">
                  <Server className="h-8 w-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">SCCM Import</h3>
                  <span className="text-xs text-amber-400/70 font-medium">Manual Mode</span>
                </div>
              </div>
              <p className="text-white/90 text-sm mb-4">
                Import application data from SCCM reports for offline management.
              </p>
              <ul className="text-white/80 text-xs space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-[#E5EFFA]/30" />
                  Upload SCCM reports
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-[#E5EFFA]/30" />
                  No cloud connection required
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-[#E5EFFA]/30" />
                  Package creation only
                </li>
              </ul>
              <div className="mt-4 flex items-center text-amber-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                Learn More
                <ArrowRight className="h-4 w-4 ml-2" />
              </div>
            </button>
          </div>

          <p className="text-white/70 text-xs text-center">
            You can change your connection type later in Settings
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
